import React, { useEffect, useState } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { TopNavigation } from '@/components/layout/TopNavigation';

const PrivacyPolicies: React.FC = () => {
  const { t, currentLanguage, changeLanguage } = useI18n();
  const [browserLanguage, setBrowserLanguage] = useState<string>('en');

  // Detectar idioma do navegador para usuários não logados
  useEffect(() => {
    const detectBrowserLanguage = () => {
      const browserLang = navigator.language || navigator.languages[0];
      const isPortuguese = browserLang.startsWith('pt');
      const detectedLang = isPortuguese ? 'pt' : 'en';
      setBrowserLanguage(detectedLang);
      
      // Se não há usuário logado, usar idioma do navegador
      if (!localStorage.getItem('user')) {
        changeLanguage(detectedLang as 'pt' | 'en');
      }
    };

    detectBrowserLanguage();
  }, [changeLanguage]);

  const currentLang = currentLanguage || browserLanguage;

  return (
    <div className="min-h-screen bg-black">
      <TopNavigation />
      
      <div className="max-w-4xl mx-auto px-4 py-8 mt-8">
        <div className="bg-gray-900 rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-white mb-8">
            {currentLang === 'pt' ? 'Políticas de Privacidade' : 'Privacy Policies'}
          </h1>
          
          <div className="prose prose-lg max-w-none">
            {currentLang === 'pt' ? (
              <div>
                <h2 className="text-2xl font-semibold text-gray-200 mb-4">
                  Políticas de Privacidade - sOSSiety
                </h2>
                
                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    1. Coleta de Dados
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Coletamos apenas os dados necessários para o funcionamento da plataforma sOSSiety, 
                    incluindo informações de perfil, dados de treinos, e interações sociais. 
                    Nunca coletamos dados desnecessários ou sensíveis.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    2. Uso dos Dados
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Seus dados são utilizados exclusivamente para:
                  </p>
                  <ul className="list-disc list-inside text-gray-400 mt-2 space-y-1">
                    <li>Fornecer funcionalidades da plataforma</li>
                    <li>Conectar você com outros praticantes de Jiu-Jitsu</li>
                    <li>Organizar e gerenciar treinos</li>
                    <li>Enviar notificações relevantes sobre sua atividade</li>
                    <li>Melhorar a experiência do usuário</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    3. Compartilhamento de Dados
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Não vendemos, alugamos ou compartilhamos seus dados pessoais com terceiros, 
                    exceto quando necessário para o funcionamento da plataforma ou quando 
                    exigido por lei. Seus dados são mantidos em segurança e privacidade.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    4. Segurança dos Dados
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Implementamos medidas de segurança técnicas e organizacionais para proteger 
                    seus dados contra acesso não autorizado, alteração, divulgação ou destruição. 
                    Utilizamos criptografia e outras tecnologias de segurança.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    5. Seus Direitos
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Você tem o direito de:
                  </p>
                  <ul className="list-disc list-inside text-gray-400 mt-2 space-y-1">
                    <li>Acessar seus dados pessoais</li>
                    <li>Corrigir dados incorretos</li>
                    <li>Solicitar a exclusão de seus dados</li>
                    <li>Retirar seu consentimento a qualquer momento</li>
                    <li>Exportar seus dados em formato legível</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    6. Cookies e Tecnologias Similares
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Utilizamos cookies e tecnologias similares para melhorar sua experiência, 
                    lembrar suas preferências e analisar o uso da plataforma. 
                    Você pode gerenciar suas preferências de cookies nas configurações do navegador.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    7. Alterações na Política
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Podemos atualizar esta política de privacidade periodicamente. 
                    Notificaremos sobre mudanças significativas através da plataforma ou por e-mail. 
                    O uso continuado da plataforma após as alterações constitui aceitação da nova política.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    8. Contato
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Se você tiver dúvidas sobre esta política de privacidade ou sobre como 
                    tratamos seus dados, entre em contato conosco através da plataforma 
                    ou pelo e-mail de suporte.
                  </p>
                </section>

                <div className="mt-12 p-6 bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-400">
                    <strong>Última atualização:</strong> {new Date().toLocaleDateString('pt-BR')}
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    <em>Esta é uma política placeholder. A política oficial será adicionada posteriormente.</em>
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h2 className="text-2xl font-semibold text-gray-200 mb-4">
                  Privacy Policies - sOSSiety
                </h2>
                
                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    1. Data Collection
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    We collect only the data necessary for the operation of the sOSSiety platform, 
                    including profile information, training data, and social interactions. 
                    We never collect unnecessary or sensitive data.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    2. Data Usage
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    Your data is used exclusively for:
                  </p>
                  <ul className="list-disc list-inside text-gray-400 mt-2 space-y-1">
                    <li>Providing platform functionality</li>
                    <li>Connecting you with other Jiu-Jitsu practitioners</li>
                    <li>Organizing and managing training sessions</li>
                    <li>Sending relevant notifications about your activity</li>
                    <li>Improving user experience</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    3. Data Sharing
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    We do not sell, rent, or share your personal data with third parties, 
                    except when necessary for platform operation or when required by law. 
                    Your data is kept secure and private.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    4. Data Security
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    We implement technical and organizational security measures to protect 
                    your data against unauthorized access, alteration, disclosure, or destruction. 
                    We use encryption and other security technologies.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    5. Your Rights
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    You have the right to:
                  </p>
                  <ul className="list-disc list-inside text-gray-400 mt-2 space-y-1">
                    <li>Access your personal data</li>
                    <li>Correct incorrect data</li>
                    <li>Request deletion of your data</li>
                    <li>Withdraw your consent at any time</li>
                    <li>Export your data in a readable format</li>
                  </ul>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    6. Cookies and Similar Technologies
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    We use cookies and similar technologies to improve your experience, 
                    remember your preferences, and analyze platform usage. 
                    You can manage your cookie preferences in your browser settings.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    7. Policy Changes
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    We may update this privacy policy periodically. 
                    We will notify about significant changes through the platform or by email. 
                    Continued use of the platform after changes constitutes acceptance of the new policy.
                  </p>
                </section>

                <section className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-300 mb-3">
                    8. Contact
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    If you have questions about this privacy policy or about how 
                    we handle your data, contact us through the platform 
                    or through the support email.
                  </p>
                </section>

                <div className="mt-12 p-6 bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-400">
                    <strong>Last updated:</strong> {new Date().toLocaleDateString('en-US')}
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    <em>This is placeholder policy. The official policy will be added later.</em>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicies;
